import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { Bell, CheckCheck, Loader2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import api, { formatApiError } from "@/lib/api";

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items);
      setUnread(data.unread_count);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const fmt = (v) => {
    try {
      return format(new Date(v), "dd MMM yyyy, HH:mm");
    } catch {
      return "";
    }
  };

  return (
    <div data-testid="notifications-page" className="space-y-6 max-w-3xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Notifications</h1>
          <p className="text-sm text-slate-600 mt-1">{unread} unread</p>
        </div>
        {unread > 0 && (
          <Button variant="outline" className="h-11" data-testid="notifications-read-all-button" onClick={markAll}>
            <CheckCheck className="h-4 w-4 mr-2" /> Mark all read
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-600" data-testid="notifications-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-16 text-center" data-testid="notifications-empty">
          <Bell className="mx-auto h-8 w-8 text-slate-300 mb-3" />
          <p className="text-sm text-slate-600">No notifications yet.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2" data-testid="notifications-list">
          {items.map((n) => (
            <Card key={n.id} data-testid={`notification-${n.id}`}
              className={n.read ? "opacity-70" : n.type === "broadcast" ? "border-l-4 border-l-red-600" : "border-l-4 border-l-primary"}>
              <CardContent className="p-4 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-slate-900 text-sm">{n.title}</p>
                    {n.type === "broadcast" && (
                      <Badge className="bg-red-600 text-white border-transparent" data-testid={`notification-urgent-${n.id}`}>Urgent</Badge>
                    )}
                    {!n.read && <Badge className="bg-primary text-white border-transparent">New</Badge>}
                  </div>
                  {n.body && <p className="text-sm text-slate-600 mt-0.5">{n.body}</p>}
                  <p className="text-xs text-slate-500 mt-1">{fmt(n.created_at)}</p>
                </div>
                <div className="flex gap-2">
                  {n.link && (
                    <Link to={n.link} className="text-xs font-medium text-primary hover:underline" data-testid={`notification-link-${n.id}`}>View</Link>
                  )}
                  {!n.read && (
                    <Button variant="ghost" className="h-8 text-xs" data-testid={`notification-read-${n.id}`} onClick={() => markRead(n.id)}>
                      Mark read
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
